import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  RevVardoc,
  RevVardocCreateRequest,
  RevVardocUpdateRequest
} from "@/api/client/models/index.ts";
import {useApiClient} from "@/hooks/api-hook.ts";

export const useCharterRevVardocStore = defineStore('charter-rev-vardoc', () => {
  const api = useApiClient()

    // State
    const revVardocs = ref<RevVardoc[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Getters
    const revVardocsCount = computed(() => revVardocs.value.length);
    const hasRevVardocs = computed(() => revVardocs.value.length > 0);
    const isLoading = computed(() => loading.value);
    const hasError = computed(() => error.value !== null);

    // Actions
    async function fetchRevVardocs(isActive: boolean = true) {
        loading.value = true;
        error.value = null;

        try {
            revVardocs.value = await api.revVardoc.get({queryParameters: {isActive}}) ?? [];
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to fetch rev vardocs';
            console.error('Error fetching rev vardocs:', err);
        } finally {
            loading.value = false;
        }
    }

    function clearRevVardocs() {
        revVardocs.value = [];
        error.value = null;
    }

    function clearError() {
        error.value = null;
    }

    async function addRevVardoc(revVardocCreateRequest: RevVardocCreateRequest) {
        loading.value = true;
        error.value = null;

        try {
            const newRevVardoc = await api.revVardoc.post(revVardocCreateRequest);
            // Refresh the rev vardocs list after adding a new rev vardoc
            await fetchRevVardocs();
            return newRevVardoc;
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to add rev vardoc';
            console.error('Error adding rev vardoc:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    async function updateRevVardoc(revVardocUpdateRequest: RevVardocUpdateRequest) {
        loading.value = true;
        error.value = null;

        try {
            await api.revVardoc.put(revVardocUpdateRequest)
            // Refresh the rev vardocs list after updating a rev vardoc
            await fetchRevVardocs();
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to update rev vardoc';
            console.error('Error updating rev vardoc:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    async function toggleRevVardocActive(id: number, isActive: boolean) {
        loading.value = true;
        error.value = null;

        try {
            await api.revVardoc.byId(id).byIsActive(isActive).post()
            // Refresh the rev vardocs list after toggling rev vardoc active status
            await fetchRevVardocs();
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to toggle rev vardoc active status';
            console.error('Error toggling rev vardoc active status:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    return {
        // State
        revVardocs,
        loading,
        error,

        // Getters
        revVardocsCount,
        hasRevVardocs,
        isLoading,
        hasError,

        // Actions
        fetchRevVardocs,
        clearRevVardocs,
        clearError,
        addRevVardoc,
        updateRevVardoc,
        toggleRevVardocActive
    };
});
